import { compact, isEmpty, round } from "lodash-es";
import {
  LOGGED_IN_PHONE_COUNTRY_CODE,
  ORDER_TYPE as ORDER_HISTORY_TYPE,
  PRICE_DECIMAL_PLACES,
} from "@/features/orders-and-requests/constants";
import type {
  CartItem,
  DeliveryRoom,
  OrderHistoryItem,
  OrderHistoryTab,
  UserDetails,
} from "@/features/orders-and-requests/types";
import type {
  BookingRoles,
  BookingSummary,
  RoomStay,
} from "@/graphql/bookingSummary";
import type {
  CreateOrderCustomerInput,
  CreateOrderInput,
  CreateOrderItemInput,
  CreateOrderRoomBookingDetailsInput,
} from "@/graphql/foodOrder";
import type { OrderHistoryItemType } from "@/graphql/orderHistory";
import {
  BILLED_ENTITY_CATEGORY,
  isExcludedRoomStatus,
} from "@/lib/constants/bookingStatus";
import { formatFullName } from "@/lib/utils/stringUtils";

const ORDER_TYPE = "room_service";
const SELLER_TYPE = "restaurant";
const ACCESS_CODE_GUEST_FIRST_NAME = "Guest";

export const resolveBilledToName = (
  bookingSummary: BookingSummary | null | undefined,
  guestName: string | undefined,
): string | undefined => {
  if (!bookingSummary) return guestName;
  switch (bookingSummary.defaultBilledEntityCategoryForExtras) {
    case BILLED_ENTITY_CATEGORY.BOOKER_COMPANY:
      return bookingSummary.companyDetails?.legalDetails?.legalName;
    case BILLED_ENTITY_CATEGORY.TRAVEL_AGENT:
      return bookingSummary.travelAgentDetails?.legalDetails?.legalName;
    case BILLED_ENTITY_CATEGORY.BOOKER:
      return formatFullName(
        bookingSummary.owner?.name?.firstName,
        bookingSummary.owner?.name?.lastName,
      );
    default:
      return guestName;
  }
};

export const resolveBilledToPhone = (
  bookingSummary: BookingSummary | null | undefined,
  guestPhone: string | null,
): string | null => {
  switch (bookingSummary?.defaultBilledEntityCategoryForExtras) {
    case BILLED_ENTITY_CATEGORY.PRIMARY_GUEST:
      return guestPhone;
    case BILLED_ENTITY_CATEGORY.BOOKER:
      return bookingSummary?.owner?.phone?.number ?? null;
    default:
      return null;
  }
};

export const getBillingConfirmationNote = (billedToName?: string): string => {
  if (billedToName) {
    return `This amount will be added to ${billedToName}'s bill.`;
  }
  return "This amount will be added to my bill.";
};

const getItemDetails = (
  item: CartItem,
  config: (typeof item.configs)[number],
  unitPretaxAmount: number,
): Record<string, unknown> => {
  if (item.type === "combo") {
    return {
      comboId: item.metadata?.menuComboId,
      prepTime: null,
      menuId: item.metadata?.menuId,
      menuComboId: item.id,
      code: item.metadata?.code,
      foodType: item.metadata?.foodType,
      price: { preTax: unitPretaxAmount },
    };
  }

  const itemDetails: Record<string, unknown> = {
    itemId: item.metadata?.menuItemId,
    prepTime: null,
    menuId: item.metadata?.menuId,
    menuItemId: item.id,
    code: item.metadata?.code,
    foodType: item.metadata?.foodType,
    price: { preTax: unitPretaxAmount },
  };

  if (config.variant) {
    itemDetails.itemVariant = {
      itemVariantId: config.variant.id,
      name: config.variant.name,
      preTaxPrice: round(
        config.variant.price?.preTaxAmount ?? 0,
        PRICE_DECIMAL_PLACES,
      ),
      skuCategoryCode: config.skuCategoryCode,
      skuId: config.skuId,
    };
  }

  const itemCustomisations = config.customizations?.flatMap((customization) =>
    customization.options.map((option) => ({
      deltaPrice: round(option.price?.preTaxAmount ?? 0, PRICE_DECIMAL_PLACES),
      itemCustomisationId: option.id,
      name: option.name,
    })),
  );

  if (!isEmpty(itemCustomisations)) {
    itemDetails.itemCustomisations = itemCustomisations;
  }

  return itemDetails;
};

const getOrderItems = (cart: Map<string, CartItem>): CreateOrderItemInput[] =>
  Array.from(cart.values()).flatMap((item) =>
    item.configs.map((config) => {
      const unitPretaxAmount = round(
        config.unitPrice?.preTaxAmount ?? 0,
        PRICE_DECIMAL_PLACES,
      );
      return {
        itemName: item.name,
        quantity: config.quantity,
        skuCategoryCode: config.skuCategoryCode ?? "",
        skuId: config.skuId ?? "",
        pretaxAmount: unitPretaxAmount,
        isComplimentary: false,
        kitchenIds: item.metadata?.kitchenIds ?? [],
        itemDetail: getItemDetails(item, config, unitPretaxAmount),
      };
    }),
  );

const findUserStayByRoles = (roomStays: RoomStay[], roles: BookingRoles) => {
  const roomStay = roomStays.find(
    (roomStay) => roomStay.id === roles.roomStayId,
  );
  const guestStay = roomStay?.guestStays.find(
    (guest) => guest.guestId === roles.guestId,
  );
  if (!roomStay || !guestStay) return null;
  return { guestStay, roomStay };
};

const findUserStayByContactDetails = (
  roomStays: RoomStay[],
  userDetails: UserDetails,
) => {
  return (
    roomStays
      .flatMap((roomStay) =>
        roomStay.guestStays.map((guestStay) => ({ guestStay, roomStay })),
      )
      .find(({ guestStay }) => {
        const isEmailMatch =
          !!userDetails.email &&
          guestStay.email?.toLowerCase() === userDetails.email.toLowerCase();
        const isPhoneMatch =
          !!userDetails.phone && guestStay.phone?.number === userDetails.phone;
        return isEmailMatch || isPhoneMatch;
      }) ?? null
  );
};

export const findLoggedInUserStayDetails = (
  roomStays: RoomStay[],
  roles: BookingRoles | null | undefined,
  userDetails: UserDetails,
) => {
  const activeRoomStays = roomStays.filter(
    (roomStay) => !isExcludedRoomStatus(roomStay.status),
  );
  return roles?.roomStayId && roles?.guestId
    ? findUserStayByRoles(activeRoomStays, roles)
    : findUserStayByContactDetails(activeRoomStays, userDetails);
};

export const getItemQuantity = (item: CartItem): number =>
  item.configs.reduce((sum, config) => sum + config.quantity, 0);

export const formatCartItemNamesWithQty = (cartItems: CartItem[]): string =>
  cartItems.map((item) => `${getItemQuantity(item)} ${item.name}`).join(", ");

export const getCartTotalItems = (cart: Map<string, CartItem>): number =>
  Array.from(cart.values()).reduce(
    (total, item) => total + getItemQuantity(item),
    0,
  );

const getRoomBookingDetails = (
  bookingId: string,
  selectedRoom: DeliveryRoom | null,
  userStayDetails: ReturnType<typeof findLoggedInUserStayDetails>,
): CreateOrderRoomBookingDetailsInput => {
  if (userStayDetails) {
    const { guestStay, roomStay } = userStayDetails;
    return {
      crsBookingId: bookingId,
      guestIds: compact([guestStay.guestId]),
      roomNumber: roomStay.roomNumber ?? "",
      roomStayId: roomStay.id,
    };
  }

  return {
    crsBookingId: bookingId,
    guestIds: compact([selectedRoom?.primaryGuest?.guestId]),
    roomNumber: selectedRoom?.roomNumber ?? "",
    roomStayId: selectedRoom?.roomStayId ?? "",
  };
};

const getCustomers = (
  userStayDetails: ReturnType<typeof findLoggedInUserStayDetails>,
  selectedRoom: DeliveryRoom | null,
): CreateOrderCustomerInput[] => {
  if (userStayDetails) {
    const { guestStay } = userStayDetails;
    return [
      {
        firstName: guestStay.name?.firstName ?? "",
        lastName: guestStay.name?.lastName || undefined,
        email: guestStay.email,
        phone: guestStay.phone
          ? {
              countryCode: guestStay.phone.countryCode,
              number: guestStay.phone.number,
            }
          : undefined,
      },
    ];
  }

  const primaryGuest = selectedRoom?.primaryGuest;
  if (!primaryGuest) return [];

  return [
    {
      firstName: primaryGuest.firstName ?? "",
      lastName: primaryGuest.lastName || undefined,
      email: primaryGuest.email,
      phone: primaryGuest.phone
        ? {
            countryCode: primaryGuest.phone.countryCode,
            number: primaryGuest.phone.number,
          }
        : undefined,
    },
  ];
};

const getRoomAccessCodeCustomers = (
  ordererContact: UserDetails | undefined,
): CreateOrderCustomerInput[] => [
  {
    firstName: ACCESS_CODE_GUEST_FIRST_NAME,
    email: ordererContact?.email ?? undefined,
    phone: ordererContact?.phone
      ? {
          countryCode: LOGGED_IN_PHONE_COUNTRY_CODE,
          number: ordererContact.phone,
        }
      : undefined,
  },
];

export const getCreateOrderPayload = ({
  cart,
  sellerId,
  bookingId,
  instructions,
  selectedRoom,
  userStayDetails,
  accessTokenRoomStay,
  ordererContact,
}: {
  cart: Map<string, CartItem>;
  sellerId: string;
  bookingId: string;
  instructions: string;
  selectedRoom: DeliveryRoom | null;
  userStayDetails: ReturnType<typeof findLoggedInUserStayDetails>;
  accessTokenRoomStay?: { roomStayId: string; roomNumber: string };
  ordererContact?: UserDetails;
}): CreateOrderInput => {
  const roomBookingDetails = accessTokenRoomStay
    ? {
        crsBookingId: bookingId,
        guestIds: null,
        roomNumber: accessTokenRoomStay.roomNumber,
        roomStayId: accessTokenRoomStay.roomStayId,
      }
    : getRoomBookingDetails(bookingId, selectedRoom, userStayDetails);

  const customers = accessTokenRoomStay
    ? getRoomAccessCodeCustomers(ordererContact)
    : getCustomers(userStayDetails, selectedRoom);

  return {
    dateTime: new Date().toISOString(),
    orderType: ORDER_TYPE,
    sellerId,
    sellerType: SELLER_TYPE,
    deliveryRoomNumber:
      selectedRoom?.roomNumber ?? roomBookingDetails.roomNumber,
    roomBookingDetails,
    remarks: instructions || undefined,
    items: getOrderItems(cart),
    customers,
  };
};

export const getOrderStatusPath = (
  basePath: string,
  orderId: string,
  type: OrderHistoryItemType,
): string =>
  type === ORDER_HISTORY_TYPE.FOOD_ORDER
    ? `${basePath}/orders/food-order/${orderId}`
    : `${basePath}/orders/service-request/${orderId}`;

export const filterOrdersByTab = (
  orders: OrderHistoryItem[],
  tab: OrderHistoryTab,
): OrderHistoryItem[] => {
  if (tab === "all") return orders;
  if (tab === "food")
    return orders.filter(
      (order) => order.type === ORDER_HISTORY_TYPE.FOOD_ORDER,
    );
  return orders.filter(
    (order) => order.type === ORDER_HISTORY_TYPE.SERVICE_REQUEST,
  );
};
